import React from 'react';
import Link from 'next/link';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  MARKS,
  INLINES,
  type Document,
} from '@contentful/rich-text-types';

/* 
# Rich Text Rendering Options:

MARKS:
 - [MARKS.BOLD] - Bold text styling
 - [MARKS.ITALIC] - Italic text styling
 - [MARKS.UNDERLINE] - Underlined text
 - [MARKS.CODE] - Inline code formatting
 - [MARKS.SUBSCRIPT] - Subscript text
 - [MARKS.SUPERSCRIPT] - Superscript text

BLOCKS:
 - [BLOCKS.PARAGRAPH] - Paragraph with custom styling
 - [BLOCKS.HEADING_1] - H1 with custom styling
 - [BLOCKS.HEADING_2] - H2 with custom styling
 - [BLOCKS.HEADING_3] - H3 with custom styling
 - [BLOCKS.HEADING_4] - H4 with custom styling
 - [BLOCKS.HEADING_5] - H5 with custom styling
 - [BLOCKS.HEADING_6] - H6 with custom styling
 - [BLOCKS.UL_LIST] - Unordered list with custom bullets
 - [BLOCKS.OL_LIST] - Ordered list with custom numbering
 - [BLOCKS.LIST_ITEM] - List item with custom styling
 - [BLOCKS.QUOTE] - Blockquote with custom styling
 - [BLOCKS.HR] - Horizontal rule with custom styling
 - [BLOCKS.TABLE] - Table with custom styling
 - [BLOCKS.TABLE_ROW] - Table row styling
 - [BLOCKS.TABLE_CELL] - Table cell styling

INLINES:
 - [INLINES.HYPERLINK] - Hyperlink with custom styling
 - [INLINES.ASSET_HYPERLINK] - Asset link handling
 - [INLINES.ENTRY_HYPERLINK] - Entry link handling
 - [INLINES.EMBEDDED_ENTRY] - Embedded entry rendering
 - [INLINES.RESOURCE_HYPERLINK] - Resource link handling

Read more in the docs: https://www.contentful.com/developers/docs/concepts/rich-text/#rendering-the-rich-text-response-from-the-graphql-api-with-linked-assets-and-entries-on-the-front-end
*/

const Bold = ({ children }) => <strong>{children}</strong>;
const Text = ({ children }) => <p>{children}</p>;

const options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
    [INLINES.HYPERLINK]: (node, children) => (
      <Link
        className='text-slate-700 hover:underline'
        href={node.data.uri}
        prefetch={false}
      >
        {children}
      </Link>
    ),
  },
};

export interface RichTextRendererProps {
  content: Document;
}

function RichTextRenderer({ content }: RichTextRendererProps) {
  const reactComponents: React.ReactNode = documentToReactComponents(
    content,
    options,
  );

  return reactComponents;
}

export default RichTextRenderer;
